import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken, CancelTokenSource } from "axios";
import { cloneDeep, groupBy } from "lodash";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { StatusCode } from "api/protocols";
import { calculateHexColorBrightness } from "helpers/Helpers";
import Spin from "components/spin/spin";
import Callout from "components/callout/Callout";
import { IDivision } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import {
  ITeeTimeSlotNew,
  ITeeTimeBookingCategory,
} from "redux/reducers/models/teetime";
import "../newTeeSheetTabs.scss";
import { ISideBySideTeeSheetDivision, ITeeTimeTabNew } from "../NewTeeSheet";
import { TTeeSheetView } from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import StarterTeeSheetTabsDivision from "./StarterTeeSheetTabsDivision";

interface INewTeeSheetTabs {
  teeSheetView: TTeeSheetView;
  sideBySideTeeSheetDivisions: ISideBySideTeeSheetDivision[];
  allDivisions: IDivision[];
  teetimeTabs: ITeeTimeTabNew[];
  teetimeBookingCategories: ITeeTimeBookingCategory[][];
  debouncedTeeTimeSearch: string;
  loadTeeSheet: () => Promise<void>;
  autoScrollNavigationKeyPrefix: string;
}

interface INewTeeSheetTabsState {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
  selectedTeeTime: ITeeTimeTabNew;
  teeSheetLeagues: ILeague[];
  teeSheetTournaments: ITournament[];
}

type TGroupedSideBySideTeeSheetDivision = ISideBySideTeeSheetDivision & {
  sortedTeeTimes: ITeeTimeTabNew[];
  groupedTeeTimes: ITeeTimeDraggable[];
};

type TDraggableSlot = Array<Array<ITeeTimeSlotNew>>;

export interface ITeeTimeDraggable extends Omit<ITeeTimeTabNew, "slots"> {
  slots: TDraggableSlot;
}


export function calculateDivisionColors(backgroundHexColor: string): { backgroundColor: string; color: string } {
  const backgroundColorBrightness = calculateHexColorBrightness(backgroundHexColor);

  if (backgroundColorBrightness) {
    return {
      backgroundColor: backgroundHexColor,
      color: backgroundColorBrightness < 50 ? "white" : "black",
    };
  } else {
    return {
      backgroundColor: "#e9e9e9",
      color: "#999999",
    };
  }
}

export default function StarterTeeSheetTabs(props: INewTeeSheetTabs) {
  const { t } = useTranslation();
  const {
    teeSheetView,
    sideBySideTeeSheetDivisions,
    allDivisions,
  } = props;


  const [state, setState] = useState<INewTeeSheetTabsState>({
    sortedTeeTimes: null,
    groupedTeeTimes: null,
    selectedTeeTime: null,
    teeSheetLeagues: undefined,
    teeSheetTournaments: undefined,
  });


  const teeSheetTabsExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  useEffect(() => {
    teeSheetTabsExitCancelSource.current = axios.CancelToken.source();

    return () => teeSheetTabsExitCancelSource.current.cancel();
  }, []);

  function groupSlots(slots: ITeeTimeSlotNew[]) {
    const bookedSlots = slots.filter(slot => slot.tee_time_booking_id != null);
    const nonBookedSlots = slots.filter(slot => slot.tee_time_booking_id == null);

    const groupedBookedSlots = Object.values(groupBy(bookedSlots, s => s.tee_time_booking_id));
    const groupedNonBookedSlots = Object.values(groupBy(nonBookedSlots, s => s.tee_time_slot_id));

    const groupedSlots = [...groupedBookedSlots, ...groupedNonBookedSlots];

    return groupedSlots;
  }

  function getSortedAndGroupedTeeTimes(
    teetimeTabs: ITeeTimeTabNew[],
    teeTimeSearch: string,
  ): { sortedTeeTimes: ITeeTimeTabNew[]; groupedTeeTimes: ITeeTimeDraggable[] } {
    if (teetimeTabs == null || teetimeTabs.length === 0) {
      return { sortedTeeTimes: [], groupedTeeTimes: [] };
    }

    let sortedTeeTimes = cloneDeep(teetimeTabs).filter(
      (teetime: ITeeTimeTabNew) => teetime.slots[0].blocked_type !== "shotgun_disabled",
    );

    if (teeTimeSearch !== "") {
      sortedTeeTimes = sortedTeeTimes.filter((teetime: ITeeTimeTabNew) =>
        teetime.slots?.some(slot =>
          `${slot.customer_first_name} ${slot.customer_last_name}`
            .toLocaleLowerCase()
            .includes(teeTimeSearch?.toLocaleLowerCase()),
        ),
      );
    }

    sortedTeeTimes.sort((current, next) => {
      const localCompare = current?.slots[0]?.start_time.localeCompare(next?.slots[0]?.start_time);

      if (localCompare === 0) {
        return current?.slots[0]?.division_id - next?.slots[0]?.division_id;
      } else {
        return localCompare;
      }
    });

    const clonedSortedTeeTimes = cloneDeep(sortedTeeTimes);

    const groupedTeeTimes: ITeeTimeDraggable[] = clonedSortedTeeTimes.map(teetime => {
      return {
        ...teetime,
        slots: groupSlots(teetime.slots),
      };
    });

    return { sortedTeeTimes, groupedTeeTimes };
  }

  const groupedSideBySideDivisions: TGroupedSideBySideTeeSheetDivision[] = useMemo(() => {
    if (teeSheetView === "side-by-side" && sideBySideTeeSheetDivisions?.length > 0) {
      return sideBySideTeeSheetDivisions.map(division => {
        return {
          ...division,
          ...getSortedAndGroupedTeeTimes(division.teetimeTabs, props.debouncedTeeTimeSearch),
        };
      });
    } else {
      return null;
    }
  }, [teeSheetView, sideBySideTeeSheetDivisions, props.debouncedTeeTimeSearch]);

  // Group slots together by booking id
  useEffect(() => {
    if (!props.teetimeTabs) {
      return;
    }

    const { sortedTeeTimes, groupedTeeTimes } = getSortedAndGroupedTeeTimes(
      props.teetimeTabs,
      props.debouncedTeeTimeSearch,
    );

    updateMainState({ sortedTeeTimes, groupedTeeTimes });
  }, [props.teetimeTabs, props.debouncedTeeTimeSearch]);

  // Load events when tee times are updated to display information about them on the tee sheet
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (!props.teetimeTabs) {
      return;
    }

    const teeTimes: ITeeTimeTabNew[] = [...props.teetimeTabs];

    // Display loader
    updateMainState({ teeSheetTournaments: undefined, teeSheetLeagues: undefined });
    // Load tee sheet tournaments and leagues if they exist
    const tournamentIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].tournament_id)
      ?.map(teetime => {
        if (teetime.slots[0].tournament_id) {
          return teetime.slots[0].tournament_id;
        }
      });
    void loadTeeSheetTournaments([...new Set(tournamentIds)], source.token);

    const leagueIds = teeTimes
      ?.filter(filteredTeeTime => filteredTeeTime?.slots[0].league_id)
      ?.map(teetime => {
        if (teetime.slots[0].league_id) {
          return teetime.slots[0].league_id;
        }
      });
    void loadTeeSheetLeagues([...new Set(leagueIds)], source.token);

    return () => source.cancel();
  }, [props.teetimeTabs]);

  // FUNCTIONS
  function updateMainState(newMainState: Partial<INewTeeSheetTabsState>) {
    setState((cur: INewTeeSheetTabsState) => {
      return { ...cur, ...newMainState };
    });
  }

  // EVENT FUNCTIONS
  async function loadTeeSheetTournaments(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const tournamentRes = await GetTournament({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetTournaments: tournamentRes.status === StatusCode.OK ? tournamentRes.data : [] });
    } else {
      updateMainState({ teeSheetTournaments: [] });
    }
  }

  async function loadTeeSheetLeagues(ids: Array<number>, token?: CancelToken) {
    if (ids?.length > 0) {
      const leagueRes = await GetLeague({ ids: ids }, false, token);

      if (token && token.reason) {
        return;
      }

      updateMainState({ teeSheetLeagues: Number(leagueRes.status) == Number(StatusCode.OK) ? leagueRes.data : [] });
    } else {
      updateMainState({ teeSheetLeagues: [] });
    }
  }


  const renderTeeSheetTabsContainer = () => {
    const spinner = (
      <div style={{ height: "24px" }}>
        <Spin />
      </div>
    );

    const errorCallout = <Callout type="error" title={t("No Tee Times Found")} />;

    if (teeSheetView === "side-by-side") {
      if (!groupedSideBySideDivisions) {
        return spinner;
      } else if (groupedSideBySideDivisions.length === 0) {
        return errorCallout;
      } else {
        return (
            <div
              className="tee-sheet-tabs-side-by-side-divisions"
              style={{
                gridTemplateColumns: `repeat(${groupedSideBySideDivisions.length}, 1fr)`,
              }}
            >
              {groupedSideBySideDivisions.map(groupedSideBySideDivision => {
                return (
                  <div
                    key={groupedSideBySideDivision.id}
                    className="tee-sheet-tabs-side-by-side-division-header"
                    style={calculateDivisionColors(groupedSideBySideDivision.division?.color)}
                  >
                    {groupedSideBySideDivision.title}
                  </div>
                );
              })}
              {groupedSideBySideDivisions.map(groupedSideBySideDivision => {
                return (
                  <StarterTeeSheetTabsDivision
                    key={groupedSideBySideDivision.id}
                    divisionClassName="tee-sheet-tabs-side-by-side-division"
                    sortedTeeTimes={groupedSideBySideDivision.sortedTeeTimes}
                    groupedTeeTimes={groupedSideBySideDivision.groupedTeeTimes}
                    teetimeBookingCategories={props.teetimeBookingCategories}
                    teeSheetLeagues={state.teeSheetLeagues}
                    teeSheetTournaments={state.teeSheetTournaments}
                    allDivisions={allDivisions}
                    updateMainState={updateMainState}
                    loadTeeSheet={props?.loadTeeSheet}
                    navigationKey={`${props.autoScrollNavigationKeyPrefix}|${groupedSideBySideDivision.id}`}
                  />
                );
              })}
            </div>
        );
      }
    } else {
      if (!state.groupedTeeTimes) {
        return spinner;
      } else if (state.groupedTeeTimes.length === 0) {
        return errorCallout;
      } else {
        return (
            <StarterTeeSheetTabsDivision
              sortedTeeTimes={state.sortedTeeTimes}
              groupedTeeTimes={state.groupedTeeTimes}
              teetimeBookingCategories={props.teetimeBookingCategories}
              teeSheetLeagues={state.teeSheetLeagues}
              teeSheetTournaments={state.teeSheetTournaments}
              allDivisions={allDivisions}
              updateMainState={updateMainState}
              loadTeeSheet={props?.loadTeeSheet}
              navigationKey={`${props.autoScrollNavigationKeyPrefix}|default`}
            />
        );
      }
    }
  };

  return (
    <>
      {renderTeeSheetTabsContainer()}
    </>
  );
}
